import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser, fetchUserDetails } from "../../../Redux/slice/features/authSlice";
import { REACT_APP_API_URL } from "../../../env";
import { validateWeakPassword } from "../../../utils/passwordValidator";
import {
  getLockoutStatus,
  registerFailure,
  clearLockout,
  formatRemainingMinutes,
} from "./lockout";

const Signin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, forcePasswordReset } = useSelector((state) => state.auth);

  const GENERIC_LOGIN_ERROR = "Invalid credentials";

  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });

  const [captchaText, setCaptchaText] = useState("");
  const [captchaId, setCaptchaId] = useState("");
  const [captchaValue, setCaptchaValue] = useState("");
  const [toast, setToast] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lockoutInfo, setLockoutInfo] = useState(() =>
    getLockoutStatus("admin", "")
  );
  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    specialChar: false,
    capitalLetter: false,
  });

  const identifier = useMemo(
    () => formData.username.trim().toLowerCase(),
    [formData.username]
  );

  const loadCaptcha = async () => {
    try {
      const res = await fetch(`${REACT_APP_API_URL}api/auth/hrms/captcha`, {
        credentials: "include",
      });
      const data = await res.json();
      setCaptchaText(data.captcha);
      setCaptchaId(data.captchaId);
      setCaptchaValue("");
    } catch {
      setToast("Unable to load captcha");
      setTimeout(() => setToast(""), 4000);
    }
  };

  useEffect(() => {
    loadCaptcha();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Track password requirements in real-time
    if (name === "password") {
      setPasswordRequirements({
        minLength: value.length >= 8,
        specialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value),
        capitalLetter: /[A-Z]/.test(value),
      });
    }
  };

  useEffect(() => {
    setLockoutInfo(getLockoutStatus("admin", identifier));
  }, [identifier]);

  const preventCopyPasteAndShortcuts = (e) => {
    const blockedKey = ["c", "v", "x"].includes(e.key?.toLowerCase());
    if ((e.ctrlKey || e.metaKey) && blockedKey) {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    const lockoutStatus = getLockoutStatus("admin", identifier);
    if (lockoutStatus.isLocked) {
      setToast(
        `Too many failed attempts. Try again in ${formatRemainingMinutes(
          lockoutStatus.remainingMs
        )}.`
      );
      setIsSubmitting(false);
      return;
    }

    if (!captchaId || !captchaValue.trim()) {
      setToast("Please enter captcha");
      setTimeout(() => setToast(""), 4000);
      setIsSubmitting(false);
      return;
    }

    // Validate password policy
    const passwordValidation = validateWeakPassword(formData.password);
    if (!passwordValidation.isValid) {
      setToast(passwordValidation.errors.join("\n"));
      setTimeout(() => setToast(""), 5000);
      setIsSubmitting(false);
      return;
    }

    try {
      const res = await dispatch(
        loginUser({
          username: formData.username,
          password: formData.password,
          captchaId,
          captchaValue: captchaValue.trim(),
        })
      );
      const resetUserId =
        res?.payload?.userId ||
        res?.payload?.UserId ||
        res?.payload?.user?.customerId ||
        res?.payload?.customerId;

      if (res.meta.requestStatus === "fulfilled") {
        if (res?.payload?.forcePasswordReset) {
          if (resetUserId) {
            sessionStorage.setItem("pendingResetUserId", String(resetUserId));
          }
          navigate("/admin/reset-password", {
            replace: true,
            state: { userId: resetUserId || null },
          });
          return;
        }

        clearLockout("admin", identifier);
        setLockoutInfo(getLockoutStatus("admin", identifier));
        sessionStorage.setItem("adminLogin", "true");
        await dispatch(fetchUserDetails());
        navigate("/", { replace: true });
      } else {
        if (res?.payload?.forcePasswordReset) {
          if (resetUserId) {
            sessionStorage.setItem("pendingResetUserId", String(resetUserId));
          }
          navigate("/admin/reset-password", {
            replace: true,
            state: { userId: resetUserId || null },
          });
          return;
        }
        const isCaptchaError = /captcha/i.test(res?.payload || "");
        if (!isCaptchaError) {
          const updated = registerFailure("admin", identifier);
          setLockoutInfo(updated);
          if (updated.isLocked) {
            setToast(
              `Too many failed attempts. Account locked for ${formatRemainingMinutes(
                updated.remainingMs
              )}.`
            );
          } else {
            setToast(
              `${GENERIC_LOGIN_ERROR}. ${updated.attemptsLeft} attempt${
                updated.attemptsLeft === 1 ? "" : "s"
              } left.`
            );
          }
        } else {
          setToast(typeof res?.payload === "string" ? res.payload : "Invalid captcha");
        }
        loadCaptcha();
        setTimeout(() => setToast(""), 5000);
      }
    } catch {
      const updated = registerFailure("admin", identifier);
      setLockoutInfo(updated);
      if (updated.isLocked) {
        setToast(
          `Too many failed attempts. Account locked for ${formatRemainingMinutes(
            updated.remainingMs
          )}.`
        );
      } else {
        setToast(
          `${GENERIC_LOGIN_ERROR}. ${updated.attemptsLeft} attempt${
            updated.attemptsLeft === 1 ? "" : "s"
          } left.`
        );
      }
      loadCaptcha();
      setTimeout(() => setToast(""), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (forcePasswordReset) {
      return;
    }

    if (user?.user?.customerId && location.pathname !== "/") {
      navigate("/", { replace: true });
    }
  }, [user?.user?.customerId, navigate, location.pathname, forcePasswordReset]);

  return (
    <div className="px-xl-5 px-4 auth-body">
      <form onSubmit={handleSubmit} autoComplete="off">
        <ul className="row g-3 list-unstyled li_animate">

          <h1 className="mb-4 text-center display-9 fw-bold text-primary">
            Admin / Manager Login
          </h1>

          <li className="col-12">
            <label className="form-label">Username</label>
            <input
              type="text"
              name="username"
              className="form-control form-control-lg"
              value={formData.username}
              onChange={handleChange}
              autoComplete="off"
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
              onKeyDown={preventCopyPasteAndShortcuts}
              required
            />
          </li>

          <li className="col-12">
            <label className="form-label">Password</label>
            <div className="input-group input-group-lg">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                className="form-control"
                value={formData.password}
                onChange={handleChange}
                autoComplete="off"
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
                onPaste={(e) => e.preventDefault()}
                onKeyDown={preventCopyPasteAndShortcuts}
                required
              />
              <button
                type="button"
                className="btn btn-light border"
                onClick={() => setShowPassword((p) => !p)}
              >
                <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`} />
              </button>
            </div>
          </li>

          {/* CAPTCHA – SAME AS HRMS */}
          <li className="col-12">
            <label className="form-label">Captcha</label>
            <div className="input-group input-group-lg">
              <span
                className="input-group-text fw-bold bg-light"
                style={{
                  letterSpacing: "6px",
                  fontFamily: "monospace",
                  userSelect: "none",
                }}
              >
                {captchaText}
              </span>

              <button
                type="button"
                className="btn"
                onClick={loadCaptcha}
                title="Refresh Captcha"
              >
                ↻
              </button>

              <input
                type="text"
                className="form-control text-center"
                placeholder="Enter captcha"
                value={captchaValue}
                onChange={(e) => setCaptchaValue(e.target.value)}
                autoComplete="off"
                required
              />
            </div>
          </li>

          <li className="col-12 my-4">
            <button
              type="submit"
              className="btn btn-lg w-100 btn-primary"
              disabled={loading || isSubmitting || lockoutInfo.isLocked}
            >
              {loading ? "Signing in..." : "SIGN IN"}
            </button>

            {identifier && !lockoutInfo.isLocked && (
              <div className="text-muted small text-center">
                {lockoutInfo.attemptsLeft} attempt{lockoutInfo.attemptsLeft === 1 ? "" : "s"} left
              </div>
            )}

            {lockoutInfo.isLocked && (
              <div className="alert alert-warning mt-3 text-center">
                Account locked. Try again in {formatRemainingMinutes(lockoutInfo.remainingMs)}.
              </div>
            )}

            {toast && (
              <div className="alert alert-danger mt-3 text-center">
                {toast}
              </div>
            )}

            <Link
              to="/hrms/login"
              onClick={() => {
                sessionStorage.removeItem("adminLogin");
                sessionStorage.removeItem("hrmsLogin");
              }}
              className="d-block mt-2 text-decoration-underline text-primary"
            >
              Login to HRMS
            </Link>
          </li>
        </ul>
      </form>
    </div>
  );
};

export default Signin;
